import React, { useRef, useCallback, useEffect, useMemo } from "react";
import {
  createButtonPressAnimation,
  attemptFullscreenToggle,
} from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openMenu, closeMenu } from "../../../../State/actions";
import {
  selectMenuActive,
  selectCriticalAnimationPlaying,
  selectIsSpinning,
  selectHoldAndWinActive,
  selectAutoPlayActive,
} from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";

const ButtonMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } = useDimensions();
  const menuActive = useSelector(selectMenuActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Calculate if button should be disabled - prevent during spins, critical animations, hold and win, or autoplay
  const isDisabled =
    criticalAnimationPlaying ||
    isSpinning ||
    holdAndWinActive ||
    autoPlayActive;

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_menu);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Calculate responsive button position based on device and orientation - memoized
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8,
        };
      } else {
        // landscape
        return {
          x: buttonConfig.width * 0.8,
          y: dimensions.height - buttonConfig.height * 0.585,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8,
        };
      } else {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8,
        };
      }
    } else {
      return {
        x: buttonConfig.width,
        y: dimensions.height - buttonConfig.height * 0.9,
      };
    }
  }, [device, orientation, dimensions.height, buttonConfig.width, buttonConfig.height]);

  const handleClick = () => {
    // Disable button during critical animations or spinning
    if (isDisabled) {
      return;
    }

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before toggling menu
    if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
      isPressAnimatingRef.current = true;
      createButtonPressAnimation(buttonSpriteRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
        onComplete: () => {
          isPressAnimatingRef.current = false;
        },
      });
    }

    if (menuActive) {
      dispatch(closeMenu());
    } else {
      dispatch(openMenu());
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (isDisabled) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter, isDisabled]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (isDisabled && buttonSpriteRef.current) {
      buttonSpriteRef.current.filters = [];
      isHoveredRef.current = false;
    }
  }, [isDisabled]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  return (
    <Container
      name="secondary_button_menu"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={1}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_menu_interactive_area"
        eventMode={isDisabled ? "none" : "static"}
        cursor={isDisabled ? "default" : "pointer"}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="button_menu_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={isDisabled ? 0.5 : 1}
        accessibleTitle={t("open_menu")}
        accessibleHint={t("open_menu")}
        resolution={2}
      />
    </Container>
  );
};

export default ButtonMenu;
